import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HeartPulse, Landmark } from "lucide-react";
import Seo from "../components/Seo";
import BookCta from "../components/BookCta";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { SITE } from "../data/site";

const SITUATIONS = [
  "Domanda di invalidità civile respinta",
  "Percentuale di invalidità riconosciuta ritenuta insufficiente",
  "Mancato riconoscimento dell'indennità di accompagnamento",
  "Revoca o modifica di una prestazione già riconosciuta",
  "Esito di revisione o altra valutazione medico-legale da esaminare",
];

const STEPS = [
  { n: "01", title: "Analisi del verbale", text: "Esame del verbale INPS e di tutta la documentazione disponibile sulla pratica." },
  { n: "02", title: "Valutazione dei presupposti", text: "Valutazione della situazione sanitaria e dei presupposti giuridici del ricorso." },
  { n: "03", title: "Individuazione della procedura", text: "Scelta della procedura concretamente applicabile al tuo tipo di controversia." },
  { n: "04", title: "Documentazione e accertamenti", text: "Eventuale raccolta di ulteriore documentazione e coordinamento con gli accertamenti medico-legali necessari." },
  { n: "05", title: "Azione giudiziaria", text: "Presentazione e gestione dell'azione giudiziaria, quando ne ricorrono i presupposti." },
  { n: "06", title: "Definizione della procedura", text: "Seguimento della procedura in ogni sua fase, fino alla sua definizione." },
];

const DOCUMENTS = [
  "Verbale INPS e tutte le comunicazioni ricevute",
  "Domanda presentata e documentazione relativa, se disponibile",
  "Referti, certificazioni e documentazione sanitaria pertinente",
  "Eventuali precedenti verbali o provvedimenti",
  "Documento di identità e codice fiscale, se necessari per la pratica",
  "Altri documenti che potranno essere richiesti in relazione al caso concreto",
];

const FAQS = [
  {
    q: "Posso fare ricorso se l'INPS ha negato l'invalidità civile?",
    a: "In molti casi un verbale di diniego può essere contestato in sede giudiziaria. Prima di tutto occorre però esaminare il verbale, la documentazione sanitaria e i termini applicabili, per capire se nel tuo caso esistono presupposti concreti.",
  },
  {
    q: "Posso fare ricorso se mi hanno riconosciuto una percentuale insufficiente?",
    a: "Sì. Il ricorso può riguardare anche la percentuale riconosciuta: se la valutazione non riflette la reale condizione di salute, è possibile chiedere un nuovo accertamento. È importante che la documentazione medica sia aggiornata e completa.",
  },
  {
    q: "Cosa posso fare se mi è stata negata l'indennità di accompagnamento?",
    a: "L'indennità di accompagnamento richiede presupposti specifici — l'impossibilità di deambulare senza l'aiuto di un accompagnatore o di compiere gli atti quotidiani della vita — diversi dalla semplice percentuale di invalidità. Un diniego può essere esaminato e, se fondato su una valutazione incompleta, contestato.",
  },
  {
    q: "Quali documenti servono per valutare un ricorso INPS?",
    a: "Servono innanzitutto il verbale INPS e le comunicazioni ricevute, la domanda presentata e la documentazione sanitaria disponibile, oltre a eventuali precedenti verbali. Nella sezione dedicata di questa pagina trovi l'elenco completo.",
  },
  {
    q: "Quanto tempo ho per contestare un verbale INPS?",
    a: "La decorrenza e la procedura dipendono dal tipo di provvedimento e dalla controversia, e spesso i termini non sono lunghi. Per questo è importante far esaminare il verbale il prima possibile: solo l'esame del documento permette di individuare il termine applicabile al tuo caso.",
  },
  {
    q: "Quanto costa un ricorso per invalidità civile?",
    a: "Non esiste una cifra standard valida per tutti: i costi dipendono dalla complessità della pratica. Lo studio illustra con chiarezza i costi dopo una prima valutazione della documentazione, prima di qualsiasi incarico.",
  },
  {
    q: "È necessario un accertamento medico-legale?",
    a: "Nel giudizio in materia di invalidità civile il giudice dispone normalmente una consulenza tecnica medica; in alcune controversie la legge può prevedere anche un accertamento tecnico in via preventiva. La procedura applicabile va verificata sul caso concreto.",
  },
  {
    q: "Come posso richiedere una valutazione del mio caso a Palermo?",
    a: "Puoi richiedere di essere ricontattato tramite il modulo contatti, telefonare allo 091 322514 oppure prenotare un appuntamento online. Lo studio si trova in Via Salvatore Meccio n. 22, a Palermo.",
  },
];

const RicorsoInvalidita = () => (
  <>
    <Seo
      title="Ricorso INPS Invalidità e Accompagnamento Palermo | Avv. Bruno"
      description="Assistenza legale a Palermo per ricorsi INPS su invalidità civile e indennità di accompagnamento. Valutazione del verbale e della documentazione. Contatta lo studio."
      path="/ricorso-inps-invalidita-accompagnamento-palermo"
      image="/images/avv-portrait.webp"
    />

    <section data-testid="landing-hero" className="bg-navy py-14 sm:py-20 text-white hero-lines">
      <div className="container-site grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <p className="kicker text-gold-light">Studio Legale Avv. Manfredi Bruno – Palermo</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold leading-[1.1] tracking-tight">
            Ricorso INPS per invalidità civile e accompagnamento a Palermo
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-slate-200">
            Assistenza legale per chi ha ricevuto un diniego, una valutazione insufficiente
            o un mancato riconoscimento delle prestazioni di invalidità civile.
          </p>
          <p className="mt-4 max-w-xl text-sm sm:text-base leading-relaxed text-slate-400">
            Lo studio valuta la tua situazione individuale — il verbale, la documentazione
            sanitaria, i precedenti — e ti illustra con chiarezza le possibili azioni da
            intraprendere.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contatti" data-testid="landing-cta-valutazione" className="btn-gold">
              Richiedi una valutazione del caso <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <BookCta testId="landing-cta-prenota" className="!bg-white !text-navy hover:!bg-gold-soft" />
          </div>
        </div>
        <div className="relative mx-auto hidden w-full max-w-sm lg:block">
          <div className="absolute -left-3 -top-3 h-full w-full rounded-2xl border border-gold/40" aria-hidden="true" />
          <img
            src="/images/avv-portrait.webp"
            alt="Avv. Manfredi Bruno, avvocato previdenzialista a Palermo"
            data-testid="landing-hero-photo"
            className="relative w-full rounded-2xl object-cover shadow-2xl"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section data-testid="landing-quando-ricorso" className="bg-white py-14 sm:py-20">
      <div className="container-site max-w-4xl">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy">
          Quando può servire un ricorso
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-700">
          Un verbale INPS sfavorevole non esaurisce necessariamente ogni possibilità di
          tutela. La legge consente di contestare la valutazione, ma occorre esaminare il
          caso concreto: non ogni verbale è impugnabile allo stesso modo, e termini,
          procedura applicabile e documentazione devono essere verificati individualmente.
          Il ricorso non è automatico né sempre conveniente: la prima cosa da fare è una
          valutazione onesta della tua situazione.
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {SITUATIONS.map((s, i) => (
            <div key={s} data-testid={`landing-situation-${i}`} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-gold-dark" aria-hidden="true" />
              <p className="text-sm font-medium leading-relaxed text-navy">{s}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="landing-differenza" className="bg-gold-soft py-14 sm:py-20">
      <div className="container-site max-w-4xl">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy">
          Invalidità civile e indennità di accompagnamento: qual è la differenza
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="landing-box-invalidita">
            <HeartPulse size={28} className="text-gold-dark" aria-hidden="true" />
            <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-navy">Invalidità civile</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              È il riconoscimento di una riduzione della capacità lavorativa (o, per minori
              e over 65, della capacità di svolgere i compiti della propria età), espresso
              in percentuale. Dalla percentuale riconosciuta dipendono le prestazioni
              economiche e i benefici accessibili, spesso con limiti reddituali.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="landing-box-accompagnamento">
            <Landmark size={28} className="text-gold-dark" aria-hidden="true" />
            <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-navy">Indennità di accompagnamento</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              È una prestazione diversa, che richiede presupposti specifici: l'impossibilità
              di deambulare senza l'aiuto permanente di un accompagnatore oppure
              l'impossibilità di compiere gli atti quotidiani della vita senza assistenza.
              Non dipende dalla sola percentuale di invalidità.
            </p>
          </div>
        </div>
        <p className="mt-6 text-base leading-relaxed text-slate-700">
          Per questo il riconoscimento di una percentuale di invalidità non comporta
          automaticamente il diritto all'accompagnamento: sono valutazioni distinte. E
          attenzione a non confondere invalidità civile, handicap ai sensi della Legge
          104/1992 e invalidità previdenziale: sono istituti diversi, con regole diverse.
          Per capire la tua posizione occorre esaminare il verbale, la documentazione
          sanitaria e la tua situazione personale.
        </p>
      </div>
    </section>

    <section data-testid="landing-come-funziona" className="bg-navy py-14 sm:py-20 text-white hero-lines">
      <div className="container-site max-w-4xl">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold">
          Come funziona il ricorso contro il verbale INPS
        </h2>
        <div className="mt-10 grid gap-8 sm:grid-cols-2">
          {STEPS.map((s) => (
            <div key={s.n} className="flex gap-5" data-testid={`landing-step-${s.n}`}>
              <span className="font-serif text-3xl font-semibold text-gold">{s.n}</span>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-10 rounded-xl border border-white/15 bg-white/5 p-5 text-sm leading-relaxed text-slate-300">
          La procedura applicabile non è identica per tutte le controversie: a seconda del
          tipo di provvedimento e di prestazione possono essere previsti passaggi diversi,
          e i termini vanno verificati sul verbale concreto. Per questo la prima fase è
          sempre l'analisi dei documenti, non la promessa di un risultato.
        </p>
      </div>
    </section>

    <section data-testid="landing-documenti" className="bg-white py-14 sm:py-20">
      <div className="container-site max-w-4xl">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy">
          I documenti utili per una prima valutazione
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-700">
          Per una prima valutazione del tuo caso è utile preparare, se disponibili:
        </p>
        <ul className="mt-6 space-y-3">
          {DOCUMENTS.map((doc, i) => (
            <li key={doc} data-testid={`landing-doc-${i}`} className="flex items-start gap-3 text-base leading-relaxed text-slate-700">
              <CheckCircle2 size={18} className="mt-1 shrink-0 text-gold-dark" aria-hidden="true" />
              {doc}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-sm leading-relaxed text-slate-500">
          La documentazione necessaria può variare da caso a caso: nessun singolo documento
          consente di prevedere l'esito di un ricorso, che va sempre valutato nella sua
          completezza.
        </p>
      </div>
    </section>

    <section data-testid="landing-perche-studio" className="bg-gold-soft py-14 sm:py-20">
      <div className="container-site grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="relative mx-auto w-full max-w-sm">
          <div className="absolute -right-3 -top-3 h-full w-full rounded-2xl border border-gold/50" aria-hidden="true" />
          <img
            src="/images/avv-reading.webp"
            alt="L'Avv. Manfredi Bruno esamina il verbale INPS e la documentazione di una pratica di invalidità civile a Palermo"
            data-testid="landing-studio-photo"
            className="relative w-full rounded-2xl object-cover shadow-xl"
            loading="lazy"
          />
        </div>
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy">
            Perché rivolgersi allo Studio Legale Avv. Manfredi Bruno
          </h2>
          <ul className="mt-6 space-y-4">
            {[
              "Attività di avvocato giuslavorista e previdenzialista a Palermo",
              "Esperienza nel diritto previdenziale e nelle controversie con l'INPS",
              "Valutazione individuale del verbale e della documentazione, mai approssimativa",
              "Chiarezza sulle possibili azioni, sui tempi e sui costi prima dell'incarico",
              "Assistenza legale nella gestione della controversia, in ogni sua fase",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-slate-700">
                <span className="mt-2 h-px w-5 shrink-0 bg-gold" aria-hidden="true" />
                {t}
              </li>
            ))}
          </ul>
          <Link
            to="/chi-sono"
            data-testid="landing-chi-sono-link"
            className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 hover:translate-x-1"
          >
            Scopri chi sono <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>

    <section data-testid="landing-faq" className="bg-white py-14 sm:py-20">
      <div className="container-site max-w-3xl">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy">
          Domande frequenti
        </h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="landing-faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`landing-faq-${i}`}>
              <AccordionTrigger className="text-left font-serif text-lg font-semibold text-navy hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-slate-700">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    <section data-testid="landing-cta-finale" className="bg-navy py-14 sm:py-20 text-white hero-lines">
      <div className="container-site max-w-3xl text-center">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold">
          Hai ricevuto un verbale INPS sfavorevole?
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
          Richiedi un primo contatto con lo Studio Legale Avv. Manfredi Bruno a Palermo
          per illustrare la tua situazione e verificare quali possibilità di assistenza
          possono essere valutate.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/contatti" data-testid="landing-final-cta-contatto" className="btn-gold">
            Richiedi di essere ricontattato
          </Link>
          <BookCta testId="landing-final-cta-prenota" />
        </div>
        <p className="mt-6 text-xs text-slate-400">
          {SITE.studio} — {SITE.address} — Tel. {SITE.phone}
        </p>
      </div>
    </section>

    <section className="border-t border-slate-200 bg-white py-10">
      <div className="container-site max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Pagine correlate</p>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
          {[
            { name: "Invalidità civile", path: "/invalidita-civile" },
            { name: "INPS e ricorsi", path: "/inps-ricorsi" },
            { name: "Diritto previdenziale", path: "/diritto-previdenziale" },
            { name: "Indebiti INPS e restituzioni", path: "/indebiti-inps-palermo" },
          ].map((l) => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`landing-related-${l.path.slice(1)}`}
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark"
            >
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
              {l.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default RicorsoInvalidita;
